/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a field inherits, and the way back** (`P3a`, S11).
 *
 * A parented artifact is resolved before the console ever sees it, so the parent's endpoint arrives
 * in a box that looks exactly like one the operator typed. This is the mark that tells them apart,
 * and the control that undoes an override — the pair that makes inheritance *usable* rather than
 * merely visible, which is the difference between a mechanism and a feature.
 *
 * Three things it deliberately does not do:
 *
 * - **It does not phrase anything.** The label and treatment come from `provenance-presentation.ts`,
 *   the same source as every other value-provenance chip, so a page cannot shorten "overridden here"
 *   and stop agreeing with the legend beside it.
 * - **It does not decide what is inherited.** That is `pathInheritance`, one projection, for the
 *   reason `isNavItemVisible` is: a surface must not be able to say what another surface hides.
 * - **It renders nothing at all for an artifact with no parent**, which is most of them. Marking
 *   every field of every standalone source is how provenance marks stop being read.
 *
 * ## Revert is offered only where it means something
 *
 * An `inherited` field has nothing to revert — it is already the parent's. Only an `overridden` one
 * gets the control, and only when the viewer may actually change the artifact. **Overridden in a
 * draft counts**: an unsaved change over an inherited value reverts locally (`restoreDraft`). The wording states
 * the outcome (*"use the value inherited"*) rather than the mechanism, and states the
 * count for a section, because one chip may stand for one row out of forty.
 */

import React from 'react';
import { Button, HStack } from '@chakra-ui/react';

import { ProvenanceChip } from './editors/ProvenanceChip';
import {
  describeRevert,
  pathInheritance,
  pathsInheritance,
  type ArtifactPathState,
} from '../../utils/artifact-inheritance';
import type { ArtifactInheritance } from '../../types';

export interface InheritanceMarkProps {
  /** The artifact's inheritance answer. Nullable — while it loads there is nothing to say. */
  inheritance: ArtifactInheritance | null | undefined;
  /**
   * Dot-joined path, or the prefix of a subtree — **or several**, for a header that stands over more
   * than one subtree. `overridden` wins across the set and the revert gives back their union.
   */
  path: string | readonly string[];
  /**
   * Called with the leaves to give back. Omit to render the mark without the control — which is the
   * right shape for a read-only viewer, and for a surface where reverting is not the gesture.
   */
  onRevert?: (paths: string[]) => void;
  /** The parent's name, when the viewer is allowed to know it. Only used in the control's title. */
  parentName?: string;
  /** True while a revert is in flight, so the control cannot be pressed twice. */
  isReverting?: boolean;
  /**
   * The answer to render, when the caller already has a **draft-aware** one.
   *
   * `inheritance` describes the stored artifact, so a surface showing an open draft knows something
   * this component cannot: that the operator has just changed this row. Passing it here keeps the
   * *presentation* in one place while letting the *answer* come from where the draft is.
   *
   * A row overridden only in the draft has no stored leaves, so the server revert has nothing to give
   * back. It said *Cancel is the gesture* until 2026-09-23, but Cancel discards the whole section; the
   * row's own way back is `restoreDraft`.
   */
  state?: ArtifactPathState;
  /**
   * The revert is pressed **inside an open draft**, where it still writes at once rather than on
   * Save (`#1126`, `useDraftRevert`) — so the control says so. Absent outside a draft, where *at once*
   * is the only thing a revert has ever meant.
   */
  revertsNow?: boolean;
  /**
   * **Revert a change made only in this draft** (Todd, 2026-09-23: *revert is "remove my override of
   * the parent", and it should be true everywhere*). Puts the stored row back into the draft. No
   * server write, because what is stored is already the parent's value.
   *
   * Used only where the stored state is `inherited`. Where the stored value is itself an override,
   * restoring it would bring back this source's own value under a button that says *inherits*, so
   * that case stays the server revert above (`onRevert`), which `useDraftRevert` then reconciles.
   * `undefined` where the surface holds no draft.
   */
  restoreDraft?: () => void;
}

export const InheritanceMark: React.FC<InheritanceMarkProps> = ({
  inheritance,
  path,
  onRevert,
  parentName,
  isReverting,
  state,
  revertsNow,
  restoreDraft,
}) => {
  const stored = Array.isArray(path)
    ? pathsInheritance(inheritance, path)
    : pathInheritance(inheritance, path as string);
  const target = state ? { ...stored, state } : stored;
  if (target.state === 'own') return null;

  const described = describeRevert(target, parentName);
  // **Two reverts, one meaning** — *use the value inherited*. What is stored decides which:
  // a stored override is given back by the server; a draft-only change over an inherited value is
  // undone here, because the stored row already is the parent's.
  const draftOnly = target.state === 'overridden' && stored.state === 'inherited' && !!restoreDraft;
  const revertTitle = draftOnly
    ? `Use the value inherited${parentName ? ` from ${parentName}` : ' from its parent'}. Undoes your unsaved change here.`
    : described && revertsNow
      ? `${described} Takes effect now, not on Save.`
      : described;
  const canRevert = target.state === 'overridden' && !!revertTitle && (draftOnly || !!onRevert);

  return (
    <HStack gap={1} align="center" display="inline-flex">
      <ProvenanceChip provenance={target.state} />
      {canRevert && (
        <Button
          size="xs"
          variant="ghost"
          height="16px"
          minW="auto"
          px={1}
          fontSize="10px"
          fontWeight="500"
          color="fg.muted"
          _hover={{ color: 'accent.solid' }}
          title={revertTitle}
          disabled={isReverting}
          onClick={(e) => {
            // The mark is rendered inside clickable headers (a section's title row), so a revert
            // must not also toggle the section the operator was reading.
            e.stopPropagation();
            if (draftOnly) restoreDraft?.();
            else onRevert?.(target.overriddenLeaves);
          }}
        >
          revert
        </Button>
      )}
    </HStack>
  );
};
